import type { AiObservationSnapshot } from '@ai-tiaozs/shared';

const CONF_LABEL: Record<string, string> = {
  high: '高置信度',
  medium: '中等',
  low: '较低',
};

interface ObservationAiSummaryProps {
  snapshot: AiObservationSnapshot | null | undefined;
  compact?: boolean;
  /** 正在从照片补充识别 */
  loading?: boolean;
}

/** 展示拍照时 AI 识别的种类 / 健康 / 虫害 */
export function ObservationAiSummary({ snapshot, compact, loading }: ObservationAiSummaryProps) {
  if (loading) {
    return (
      <p className="text-[11px] text-slate-400">
        🤖 正在从照片补充 AI 识别…
      </p>
    );
  }
  if (!snapshot?.species && !snapshot?.healthStatus && snapshot?.hasPestIssue === undefined) {
    return compact ? (
      <p className="text-[11px] text-slate-400">🤖 暂无识别记录</p>
    ) : (
      <p className="text-[11px] text-slate-400">暂无 AI 识别记录(旧数据或未识别)</p>
    );
  }

  if (compact) {
    return (
      <p className="text-[11px] leading-snug text-slate-600">
        {snapshot.species && (
          <span>
            🤖 {snapshot.species}
            {snapshot.confidence ? ` · ${CONF_LABEL[snapshot.confidence] ?? snapshot.confidence}` : ''}
          </span>
        )}
        {snapshot.healthStatus && (
          <span className={snapshot.healthStatus === '健康' ? ' text-emerald-700' : ' text-amber-700'}>
            {' '}
            · {snapshot.healthStatus}
          </span>
        )}
        {snapshot.hasPestIssue ? (
          <span className=" text-teamA-danger"> · ⚠️ {snapshot.pestSummary ?? '有虫害'}</span>
        ) : (
          <span className=" text-emerald-600"> · ✅ 无虫害</span>
        )}
      </p>
    );
  }

  return (
    <div className="space-y-1.5 rounded-md border border-teamB-primary/20 bg-teamB-primary/5 px-3 py-2 text-xs">
      <p className="font-medium text-teamB-primary">📷 拍照时 AI 识别</p>
      {snapshot.species && (
        <p className="text-slate-700">
          种类:{snapshot.species}
          {snapshot.confidence && (
            <span className="ml-1 text-slate-500">({CONF_LABEL[snapshot.confidence]})</span>
          )}
        </p>
      )}
      {snapshot.healthStatus && (
        <p className="text-slate-700">健康:{snapshot.healthStatus}</p>
      )}
      {snapshot.hasPestIssue ? (
        <div className="text-teamA-danger">
          <p>⚠️ 虫害:{snapshot.pestSummary ?? '检测到异常'}</p>
          {snapshot.pests?.[0]?.treatment && (
            <p className="mt-0.5 text-[11px] text-slate-600">建议:{snapshot.pests[0].treatment}</p>
          )}
        </div>
      ) : (
        <p className="text-emerald-700">✅ 未发现明显虫害</p>
      )}
    </div>
  );
}
